// 国際宇宙ステーション（ISS）の通過予報
// 軌道要素（TLE）を鍵なしの公開API（CelesTrak → wheretheiss.at）から取得し、satellite.js で端末内計算する。
import { twoline2satrec, propagate, gstime, eciToEcf, ecfToLookAngles, degreesToRadians, sunPos, jday } from 'satellite.js';
import { Body, Equator, Horizon, Observer } from 'astronomy-engine';
import { dirName } from './sky';

const TLE_KEY = 'ki_iss_tle_v1';
const TLE_MAX_AGE = 12 * 3600 * 1000;

type Tle = { l1: string; l2: string; fetchedAt: number };

async function fetchTle(): Promise<Tle | null> {
  try {
    const c = localStorage.getItem(TLE_KEY);
    if (c) { const v: Tle = JSON.parse(c); if (Date.now() - v.fetchedAt < TLE_MAX_AGE) return v; }
  } catch { /* noop */ }
  let tle: Tle | null = null;
  try {
    const r = await fetch('https://celestrak.org/NORAD/elements/gp.php?CATNR=25544&FORMAT=TLE');
    if (r.ok) {
      const lines = (await r.text()).split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
      const l1 = lines.find((s) => s.startsWith('1 25544'));
      const l2 = lines.find((s) => s.startsWith('2 25544'));
      if (l1 && l2) tle = { l1, l2, fetchedAt: Date.now() };
    }
  } catch { /* 次の取得元へ */ }
  if (!tle) {
    try {
      const r = await fetch('https://api.wheretheiss.at/v1/satellites/25544/tles');
      if (r.ok) { const d = await r.json(); if (d.line1 && d.line2) tle = { l1: d.line1, l2: d.line2, fetchedAt: Date.now() }; }
    } catch { /* noop */ }
  }
  if (tle) { try { localStorage.setItem(TLE_KEY, JSON.stringify(tle)); } catch { /* noop */ } }
  return tle;
}

const EARTH_RADIUS_KM = 6378.137;

/** 地球の影（円柱近似）に入っているか。rsun は同じ ECI 座標系の太陽方向ベクトル */
function inEarthShadow(rsun: number[], pos: { x: number; y: number; z: number }): boolean {
  const n = Math.hypot(rsun[0], rsun[1], rsun[2]);
  const s = [rsun[0] / n, rsun[1] / n, rsun[2] / n];
  const d = pos.x * s[0] + pos.y * s[1] + pos.z * s[2];
  if (d >= 0) return false; // 太陽側にいる
  const px = pos.x - d * s[0], py = pos.y - d * s[1], pz = pos.z - d * s[2];
  return Math.hypot(px, py, pz) < EARTH_RADIUS_KM;
}

export type IssPass = {
  start: Date;
  end: Date;
  maxElevation: number; // 度
  fromDir: string;
  toDir: string;
};

/** 見える通過（観測地が暗く、ISS が日に照らされ、高度10°以上）を now から hours 時間ぶん探す */
export function findIssPasses(l1: string, l2: string, now: Date, lat: number, lng: number, hours = 24): IssPass[] {
  const satrec = twoline2satrec(l1, l2);
  const gd = { latitude: degreesToRadians(lat), longitude: degreesToRadians(lng), height: 0.05 };
  const obs = new Observer(lat, lng, 0);
  const STEP = 20 * 1000;
  const passes: IssPass[] = [];
  let cur: { start: Date; end: Date; max: number; fromAz: number; toAz: number } | null = null;

  for (let ms = 0; ms <= hours * 3600 * 1000; ms += STEP) {
    const t = new Date(now.getTime() + ms);
    const pv = propagate(satrec, t);
    let visible = false;
    let el = 0, az = 0;
    if (pv && pv.position && typeof pv.position !== 'boolean') {
      const look = ecfToLookAngles(gd, eciToEcf(pv.position, gstime(t)));
      el = (look.elevation * 180) / Math.PI;
      az = (look.azimuth * 180) / Math.PI;
      if (el >= 10) {
        const sunEq = Equator(Body.Sun, t, obs, true, true);
        const sunAlt = Horizon(t, obs, sunEq.ra, sunEq.dec, 'normal').altitude;
        if (sunAlt <= -6) {
          visible = !inEarthShadow(sunPos(jday(t)).rsun, pv.position);
        }
      }
    }
    if (visible) {
      if (!cur) cur = { start: t, end: t, max: el, fromAz: az, toAz: az };
      cur.end = t; cur.toAz = az; if (el > cur.max) cur.max = el;
    } else if (cur) {
      if (cur.end.getTime() - cur.start.getTime() >= 60 * 1000) {
        passes.push({ start: cur.start, end: cur.end, maxElevation: Math.round(cur.max), fromDir: dirName(cur.fromAz), toDir: dirName(cur.toAz) });
      }
      cur = null;
    }
  }
  return passes;
}

export async function getNextIssPass(now: Date, lat: number, lng: number): Promise<IssPass | null> {
  const tle = await fetchTle();
  if (!tle) return null;
  const passes = findIssPasses(tle.l1, tle.l2, now, lat, lng, 24);
  return passes[0] || null;
}
